import { IKMLElement, KMLElement } from '../kml';
import { Boundary, Zone } from '../db';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { cleanBoundary } from '../utils';
import { alertWarn } from '../alertDispatcher';
import BoundaryClass from '../db/BoundaryClass';
import { Coordinate } from 'ol/coordinate';
import { MINIMUM_COORDINATES_FOR_VALID_BOUNDARY } from '../constants';

export function to_kml(this: BoundaryClass) {
  const lr = KMLElement.element('LinearRing');
  const coords = KMLElement.subelement(lr, 'coordinates');
  coords.setText('');

  for (const [i, coord] of this.coordinates.entries()) {
    coords.setText(`${coords.getText()}${i > 0 ? ' ' : ''}${coord[1]},${coord[0]},0`);
  }

  return lr;
}

export async function load_kml(
  linearring: IKMLElement,
  zone: Zone,
  outer: boolean,
  poly_id: number,
): Promise<[Boundary | undefined, string[]]> {
  let boundary: Boundary | undefined = undefined;
  const error_messages: string[] = [];

  const coords = linearring.find('coordinates') ? linearring.find('coordinates')?.getText() : '';

  if (!!coords) {
    let boundary_coordinates = coords
      .trim()
      .split(' ')
      .filter((coord_set) => !!coord_set)
      .map((coord_set) => [parseFloat(coord_set.split(',')[1]), parseFloat(coord_set.split(',')[0])]) as Coordinate[];
    boundary_coordinates = cleanBoundary(boundary_coordinates); // clean the boundary of duplicates

    boundary = await Boundary.new_boundary(boundary_coordinates, zone, outer, poly_id);
  } else {
    error_messages.push(`ERROR: no coordinates found for boundary in '${zone.zone_name}'`);
  }

  // return loaded boundaries and error/warning messages
  return [boundary, error_messages];
}

export function contains(this: BoundaryClass, lat: number, lon: number) {
  // Instead of doing all of the transforms, we can just create simple polygon objects that wrap the
  // existing coordinates. This should be much faster
  const contains = booleanPointInPolygon([lat, lon], {
    type: 'Polygon',
    coordinates: [this.coordinates],
  });

  return contains;
}

export async function new_boundary(
  boundary_coordinates: Coordinate[],
  zone: Zone,
  outer: boolean,
  poly_id: number,
): Promise<Boundary | undefined> {
  if (boundary_coordinates.length < MINIMUM_COORDINATES_FOR_VALID_BOUNDARY) {
    alertWarn(`Could not create boundary, needs ${MINIMUM_COORDINATES_FOR_VALID_BOUNDARY} or more coordinates`);

    return;
  }

  const boundary = Boundary.create();
  boundary.boundary_id = zone.getMission()!.assign_id();
  boundary.poly_id = poly_id;

  if (outer) {
    boundary.OuterZone_id = zone.instance_id;
  } else {
    boundary.InnerZone_id = zone.instance_id;
  }

  boundary.coordinates = boundary_coordinates;

  return boundary;
}
